import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import {
  formatEuro,
  LIBRARY_UNIT_LABELS,
  LIBRARY_UNITS,
  quantityText,
  replacementQuantityDiffers,
  todayIso,
  type AssetReplacementDto,
  type LibraryUnit,
} from "@klantpunt/shared";
import { api, ApiError } from "../../lib/api";
import { can, hasModule, useMe } from "../../lib/auth";
import { useToast } from "../../lib/toast";
import { parseEuro } from "../../pages/Platform";

/**
 * MJOP-velden bij een asset en een bevinding (docs/ASSETS-MJOP.md, "Invoer"). Alleen zichtbaar met
 * minimaal *bekijken* op MJOP; wijzigen met *bewerken*. Bedragen excl. btw.
 */
const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Er ging iets mis");
export const euroInput = (c: number | null | undefined) => (c == null ? "" : (c / 100).toFixed(2).replace(".", ","));
const thisYear = () => Number(todayIso().slice(0, 4));
const intOrNull = (v: string) => (v.trim() && /^\d+$/.test(v.trim()) ? Number(v.trim()) : null);
const invalidateMjop = (qc: ReturnType<typeof useQueryClient>) => {
  void qc.invalidateQueries({ queryKey: ["mjop"] });
  void qc.invalidateQueries({ queryKey: ["budget-items"] });
};

/** Vervanging van een asset (MJOP); alleen opgehaald met recht op de module. */
export function useAssetReplacement(assetId: string) {
  const { data: me } = useMe();
  return useQuery({
    queryKey: ["asset-replacement", assetId],
    queryFn: () => api<AssetReplacementDto>(`/assets/${assetId}/replacement`),
    enabled: hasModule(me, "mjop"),
  });
}

/**
 * Blok *Onderhoud en vervanging* bij een asset: levensduur en vervangingsprijs → vervangingspost.
 * Het formulier (met het voorstel uit de kennisbank) opent pas bij Bewerken, of meteen als er nog niets is ingevuld.
 * Bedragen zien alleen gebruikers die assets beheren (niet klanten of uitvoerders).
 */
export function ReplacementSection({ assetId }: { assetId: string }) {
  const { data: me } = useMe();
  const see = hasModule(me, "mjop");
  const canEdit = hasModule(me, "mjop", "edit");
  const q = useAssetReplacement(assetId);
  if (!see || !q.data) return null;
  return (
    <ReplacementForm
      key={JSON.stringify(q.data.item)}
      assetId={assetId}
      r={q.data}
      canEdit={canEdit}
      showCost={can(me, "asset.manage")}
    />
  );
}

function ReplacementForm({
  assetId,
  r,
  canEdit,
  showCost,
}: {
  assetId: string;
  r: AssetReplacementDto;
  canEdit: boolean;
  showCost: boolean;
}) {
  // Een onderdeel valt standaard onder de hoofdasset: het formulier pas openen als je een eigen post wilt.
  const [editing, setEditing] = useState(canEdit && !r.item && !r.parent);
  const qc = useQueryClient();
  const toast = useToast();
  const [installed, setInstalled] = useState(r.installedYear?.toString() ?? "");
  const [life, setLife] = useState(r.lifespanYears?.toString() ?? "");
  const [price, setPrice] = useState(euroInput(r.unitPriceCents));
  const [qty, setQty] = useState(String(r.quantity).replace(".", ","));
  const [unit, setUnit] = useState<LibraryUnit>((r.unit as LibraryUnit) ?? "st");
  const [level, setLevel] = useState(r.priceLevelYear?.toString() ?? "");
  const [version, setVersion] = useState<number | null>(r.libraryVersionUsed);
  const save = useMutation({
    mutationFn: () =>
      api<AssetReplacementDto>(`/assets/${assetId}/replacement`, {
        method: "PUT",
        body: {
          installedYear: intOrNull(installed),
          lifespanYears: intOrNull(life),
          unitPriceCents: price.trim() ? parseEuro(price) : null,
          quantity: Number(qty.replace(",", ".")) || 1,
          unit,
          priceLevelYear: intOrNull(level),
          ...(version ? { libraryVersionUsed: version } : {}),
        },
      }),
    onSuccess: (d) => {
      qc.setQueryData(["asset-replacement", assetId], d);
      invalidateMjop(qc);
      toast(d.item ? `Vervanging gepland in ${d.item.firstYear}` : "Vervanging opgeslagen");
    },
    onError: (e) => toast(errMsg(e)),
  });
  // Eigen aantal terugzetten naar dat van de asset, zonder het formulier te openen.
  const reset = useMutation({
    mutationFn: () =>
      api<AssetReplacementDto>(`/assets/${assetId}/replacement`, {
        method: "PUT",
        body: {
          lifespanYears: r.lifespanYears,
          unitPriceCents: r.unitPriceCents,
          quantity: r.assetQuantity ?? r.quantity,
          unit: (r.assetUnit as LibraryUnit | null) ?? (r.unit as LibraryUnit),
          priceLevelYear: r.priceLevelYear,
        },
      }),
    onSuccess: (d) => {
      qc.setQueryData(["asset-replacement", assetId], d);
      invalidateMjop(qc);
      toast("Aantal van de asset overgenomen");
    },
    onError: (e) => toast(errMsg(e)),
  });
  const asset = { quantity: r.assetQuantity, unit: r.assetUnit };
  const assetQty = r.assetQuantity != null && r.assetQuantity > 0 ? quantityText(r.assetQuantity, r.assetUnit) : null;
  const formQty = Number(qty.replace(",", "."));
  const formDiffers = Boolean(formQty) && replacementQuantityDiffers(asset, formQty, unit);
  const s = r.suggestion;
  const badPrice = price.trim() !== "" && parseEuro(price) === null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!badPrice) save.mutate();
  };
  return (
    <section className="sec" aria-label="Onderhoud en vervanging">
      <p className="label">Onderhoud en vervanging</p>
      {r.item ? (
        <p className="small">
          Vervanging gepland in <strong>{r.item.firstYear}</strong>
          {r.item.cycleYears && `, daarna elke ${r.item.cycleYears} jaar`}
          {showCost &&
            r.unitPriceCents != null &&
            ` · ${formatEuro(Math.round(r.unitPriceCents * r.quantity))} op prijspeil`}
          {showCost &&
            r.item.indexedCents != null &&
            r.unitPriceCents != null &&
            r.item.indexedCents !== Math.round(r.unitPriceCents * r.quantity) &&
            `, ${formatEuro(r.item.indexedCents)} in ${r.item.firstYear} met index`}
        </p>
      ) : r.parent ? (
        <p className="small" data-testid="part-replacement">
          Valt onder de vervanging van{" "}
          <strong>
            {r.parent.code} {r.parent.name}
          </strong>
          {r.parent.replacementYear ? ` (gepland in ${r.parent.replacementYear})` : " (nog niet gepland)"}. Vul hier
          alleen iets in als dit onderdeel een eigen grote post is, bijvoorbeeld een pomp met een kortere levensduur.
          Die telt dan apart mee in het MJOP; haal het bedrag dan uit de prijs van de hoofdasset.
        </p>
      ) : (
        <p className="muted small">Nog geen vervanging in het MJOP. Vul levensduur en vervangingsprijs in.</p>
      )}
      {r.item && r.item.firstYear > r.horizonEndYear && (
        <p className="hint" data-testid="replacement-after-horizon">
          {r.item.firstYear} valt na de planhorizon van het MJOP (tot en met {r.horizonEndYear}). De vervanging staat
          daarom nog niet in de grafiek en de tabel per jaar, maar telt wel mee in de reservering. De planhorizon stel
          je in bij MJOP, ⋯, Instellingen.
        </p>
      )}
      {r.item && r.parent && (
        <p className="muted small">Eigen post naast de vervanging van {r.parent.code}; telt apart mee in het MJOP.</p>
      )}
      {r.item && r.quantityOwn && !editing && (
        <p className="small" data-testid="replacement-own-quantity">
          <span className="chip tone tone-warn">Eigen aantal</span> In het MJOP {quantityText(r.quantity, r.unit)}, de
          asset heeft {assetQty}.{" "}
          {canEdit && (
            <button type="button" className="linkbtn inline" disabled={reset.isPending} onClick={() => reset.mutate()}>
              Aantal van de asset gebruiken
            </button>
          )}
        </p>
      )}
      {canEdit && !editing && (
        <div>
          <button type="button" className="btn small" onClick={() => setEditing(true)}>
            {r.parent && !r.item ? "Eigen vervanging toevoegen" : "Vervanging bewerken"}
          </button>
        </div>
      )}
      {editing && s && (s.lifespanYears || s.unitPriceCents) && (
        <div className="obcard sugg">
          <p className="small">
            <strong>Uit de kennisbank</strong> (NL-SfB {s.from})
            {s.indicative && <span className="chip small">indicatief</span>}
          </p>
          <p className="small muted">
            {s.lifespanYears && `Levensduur ${s.lifespanYears} jaar`}
            {s.lifespanYears && s.unitPriceCents != null && " · "}
            {s.unitPriceCents != null &&
              `${formatEuro(s.unitPriceCents)}${s.unit ? ` per ${LIBRARY_UNIT_LABELS[s.unit as LibraryUnit]}` : ""}`}
            {s.priceLevelYear && ` · prijspeil ${s.priceLevelYear}`}
          </p>
          <div>
            <button
              type="button"
              className="btn small"
              onClick={() => {
                if (s.lifespanYears) setLife(String(s.lifespanYears));
                if (s.unitPriceCents != null) setPrice(euroInput(s.unitPriceCents));
                if (s.unit) setUnit(s.unit as LibraryUnit);
                if (s.priceLevelYear) setLevel(String(s.priceLevelYear));
                setVersion(s.version);
              }}
            >
              Overnemen
            </button>
          </div>
        </div>
      )}
      {editing ? (
        <form className="formgrid" onSubmit={submit} aria-label="Vervanging">
          <label className="field">
            <span className="label">Bouw-/installatiejaar</span>
            <input inputMode="numeric" value={installed} onChange={(e) => setInstalled(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">Levensduur (jaar)</span>
            <input inputMode="numeric" value={life} onChange={(e) => setLife(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">Vervangingsprijs per eenheid (€)</span>
            <input
              inputMode="decimal"
              value={price}
              aria-invalid={badPrice}
              onChange={(e) => setPrice(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="label">Hoeveelheid</span>
            <input inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} />
            {unit === "post" ? (
              <span className="hint">Post: één bedrag, los van het aantal van de asset.</span>
            ) : assetQty ? (
              formDiffers ? (
                <span className="hint" data-testid="replacement-qty-hint">
                  Wijkt af van de asset ({assetQty}); wordt bewaard als eigen aantal.{" "}
                  <button
                    type="button"
                    className="linkbtn inline"
                    onClick={() => {
                      setQty(String(r.assetQuantity).replace(".", ","));
                      if (r.assetUnit) setUnit(r.assetUnit as LibraryUnit);
                    }}
                  >
                    Aantal van de asset gebruiken
                  </button>
                </span>
              ) : (
                <span className="hint">Van de asset; past mee aan als de asset verandert.</span>
              )
            ) : null}
          </label>
          <label className="field">
            <span className="label">Eenheid</span>
            <select value={unit} onChange={(e) => setUnit(e.target.value as LibraryUnit)}>
              {LIBRARY_UNITS.map((u) => (
                <option key={u} value={u}>
                  {LIBRARY_UNIT_LABELS[u]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Prijspeil (jaar)</span>
            <input
              inputMode="numeric"
              value={level}
              placeholder={String(thisYear())}
              onChange={(e) => setLevel(e.target.value)}
            />
          </label>
          <div className="btnrow">
            <button type="submit" className="btn small primary" disabled={save.isPending || badPrice}>
              Vervanging opslaan
            </button>
            {r.item && (
              <button type="button" className="btn small ghost" onClick={() => setEditing(false)}>
                Annuleren
              </button>
            )}
          </div>
        </form>
      ) : (
        r.lifespanYears && (
          <p className="small muted">
            Levensduur {r.lifespanYears} jaar
            {showCost && r.unitPriceCents != null && ` · ${formatEuro(r.unitPriceCents)} per ${r.unit}`}
          </p>
        )
      )}
    </section>
  );
}

interface EstimateDto {
  estimatedCostCents: number | null;
  plannedRepairYear: number | null;
  item: { id: string; firstYear: number; status: string } | null;
}

/** Raming herstelkosten bij een bevinding: één optioneel veld plus het jaar. */
export function EstimateSection({ issueId, priority }: { issueId: string; priority: string }) {
  const { data: me } = useMe();
  const see = hasModule(me, "mjop");
  const q = useQuery({
    queryKey: ["issue-estimate", issueId],
    queryFn: () => api<EstimateDto>(`/issues/${issueId}/estimate`),
    enabled: see,
  });
  if (!see || !q.data) return null;
  return (
    <EstimateForm
      key={`${q.data.estimatedCostCents}-${q.data.plannedRepairYear}`}
      issueId={issueId}
      d={q.data}
      urgent={priority === "P1"}
      canEdit={hasModule(me, "mjop", "edit")}
    />
  );
}

function EstimateForm({
  issueId,
  d,
  urgent,
  canEdit,
}: {
  issueId: string;
  d: EstimateDto;
  urgent: boolean;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [cost, setCost] = useState(euroInput(d.estimatedCostCents));
  const [year, setYear] = useState(String(d.plannedRepairYear ?? thisYear()));
  const save = useMutation({
    mutationFn: () =>
      api<EstimateDto>(`/issues/${issueId}/estimate`, {
        method: "PUT",
        body: { estimatedCostCents: cost.trim() ? parseEuro(cost) : null, plannedRepairYear: intOrNull(year) },
      }),
    onSuccess: (r) => {
      qc.setQueryData(["issue-estimate", issueId], r);
      invalidateMjop(qc);
      toast(r.item ? `Herstel in het MJOP voor ${r.item.firstYear}` : "Raming verwijderd");
    },
    onError: (e) => toast(errMsg(e)),
  });
  const bad = cost.trim() !== "" && parseEuro(cost) === null;
  if (!canEdit)
    return d.estimatedCostCents != null ? (
      <section className="sec">
        <p className="label">Raming herstelkosten</p>
        <p className="small">
          {formatEuro(d.estimatedCostCents)} excl. btw · {d.plannedRepairYear}
        </p>
      </section>
    ) : null;
  return (
    <section className="sec" aria-label="Raming herstelkosten">
      <p className="label">Raming herstelkosten</p>
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          if (!bad) save.mutate();
        }}
      >
        <label className="field grow">
          <span className="label">Bedrag excl. btw (€)</span>
          <input
            inputMode="decimal"
            value={cost}
            aria-invalid={bad}
            placeholder="optioneel"
            onChange={(e) => setCost(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="label">Jaar</span>
          <input
            inputMode="numeric"
            value={urgent ? String(thisYear()) : year}
            disabled={urgent}
            title={urgent ? "Spoed: altijd het lopende jaar" : undefined}
            onChange={(e) => setYear(e.target.value)}
            size={6}
          />
        </label>
        <button type="submit" className="btn small" disabled={save.isPending || bad}>
          Opslaan
        </button>
      </form>
      {d.item && (
        <p className="hint">In het MJOP als herstelpost ({d.item.status === "done" ? "uitgevoerd" : "gepland"}).</p>
      )}
    </section>
  );
}
